import React, { useEffect, useRef, useState } from 'react';
import { getObjectURLFromMediaURLWithCaching } from 'util/media-services';
import { styled } from '@compiled/react';
import { useFragment } from 'react-relay';
import { graphql } from 'relay-runtime';
import Grid from '@atlaskit/grid';
import { Box, xcss } from '@atlaskit/primitives';
import { SubsectionViewRenderer } from '../subsection-view-renderer';
import { useSectionAndElementId } from '../use-section-element-id';
import type { sectionViewRendererFragment$key } from './__generated__/sectionViewRendererFragment.graphql';

export const SECTION_IDENTIFIER = 'layout-section';

interface SectionProps {
    section: sectionViewRendererFragment$key;
}

const SectionViewRenderer: React.FC<SectionProps> = ({ section }) => {
    const [cachedBannerUrl, setCachedBannerUrl] = useState<string | null>();
    const sectionData = useFragment(
        graphql`
            fragment sectionViewRendererFragment on HelpLayoutSection {
                __id
                subsections {
                    __id
                    ...subsectionViewRendererFragment
                    elements {
                        __id
                        __typename
                    }
                }
                visualConfig {
                    backgroundColor
                    backgroundImage {
                        url
                    }
                    hidden
                }
            }
        `,
        section
    );

    const sectionRef = useRef<HTMLElement>(null);

    const subsections = sectionData?.subsections;
    const backgroundColor = sectionData?.visualConfig?.backgroundColor;
    const backgroundImageUrl = sectionData?.visualConfig?.backgroundImage?.url;

    useEffect(() => {
        if (!!backgroundImageUrl && backgroundImageUrl.startsWith('blob:')) {
            setCachedBannerUrl(backgroundImageUrl);
        } else if (!!backgroundImageUrl) {
            getObjectURLFromMediaURLWithCaching(backgroundImageUrl)
                .then((url) => setCachedBannerUrl(url))
                .catch((errorMessage: string) => {
                    throw new Error(`Could not load help center banner. Reason: ${errorMessage}`);
                });
        } else {
            setCachedBannerUrl(null);
        }
    }, [backgroundImageUrl, setCachedBannerUrl]);

    const sectionId = sectionData.__id;
    const element = sectionData?.subsections?.[0]?.elements?.[0];
    useSectionAndElementId(element?.__typename || '', sectionId, element?.__id || '');
    const isSectionHidden = Boolean(sectionData?.visualConfig?.hidden);

    const sectionCustomProp = React.useMemo(() => {
        return {
            style: {
                backgroundImage: cachedBannerUrl && !isSectionHidden ? `url(${cachedBannerUrl})` : 'inherit',
            },
        };
    }, [cachedBannerUrl, isSectionHidden]);

    if (!sectionData || !sectionId) {
        return null;
    }
    if (!subsections || isSectionHidden) {
        return null;
    }
    return (
        <BoxSectionWrapper
            backgroundColor={backgroundColor}
            isHidden={isSectionHidden}
            data-testid="layout-section-box-wrapper"
        >
            <Box
                id={sectionId}
                ref={sectionRef}
                as={'section'}
                testId={SECTION_IDENTIFIER}
                xcss={styledSection}
                {...sectionCustomProp}
            >
                <Grid hasInlinePadding>
                    {subsections.map((subsection) => {
                        if (!subsection) {
                            return null;
                        }

                        return <SubsectionViewRenderer key={subsection.__id} subsection={subsection} />;
                    })}
                </Grid>
            </Box>
        </BoxSectionWrapper>
    );
};

export default SectionViewRenderer;

const styledSection = xcss({
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
});

interface BoxSectionWrapperProps {
    backgroundColor?: string | null;
    isHidden?: boolean;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const BoxSectionWrapper = styled.div<BoxSectionWrapperProps>({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Disabled to rollout go/ui-styling-standard tooling, please resolve
    backgroundColor: (props) => (props.backgroundColor && !props.isHidden ? props.backgroundColor : 'none'),
});
